import { compareIds } from '#~/models/index.js';
import { Sel, STagNew } from '#~/stag/index.js';
import { Group } from './group/index.js';
import type { ListProps } from './types.js';
import * as DropdownMenu from '@studio/design-system/dropdown-menu';
import { Motions } from '@studio/design-system/motion';
import { Reorder, useDragging, useOrderedIds } from '@studio/design-system/motion/reorder';
import type { Group as M_Group } from '@studio/models/control/preset';
import { useLatestFn, useLazyRef } from '@studio/react-utils/hooks';
import { dequal } from 'dequal/lite';
import type { ReactElement } from 'react';
import { memo } from 'react';
import { twMerge } from 'cn';

/** @internal */
const Inner = ({ className, groups, handler, selectedId, ...props }: ListProps): ReactElement => {
  // Drag/Drop
  const [orderedIds, setOrderedIds] = useOrderedIds(groups.map((µ) => µ.id));
  const [isDragging, setDragging] = useDragging();

  const handleDragStart = useLatestFn(() => {
    setDragging(true);
  });

  const handleDragEnd = useLatestFn(() => {
    setDragging(false);

    // Has the order actually changed?
    if (!compareIds(groups, orderedIds)) {
      handler.onReorder(orderedIds);
    }
  });

  const handleSelect = useLatestFn((groupId: M_Group['id']) => {
    // Ignore click events while dragging.
    if (!isDragging) {
      handler.onSelect(groupId);
    }
  });

  // Override `onSelect` handler.
  const passthru = useLazyRef(() => ({
    ...handler,
    onSelect: handleSelect
  })).current;

  return (
    <div className={twMerge('flex', className)} {...props}>
      <Reorder.Group
        axis='x'
        className='flex grow justify-center gap-12'
        layoutScroll
        onReorder={setOrderedIds}
        values={orderedIds}
      >
        {orderedIds.map((µ) => {
          const group = groups.find((g) => g.id === µ);

          // Sanity check
          if (!group) {
            return null;
          }

          return (
            <Reorder.Item
              key={group.id}
              onDragEnd={handleDragEnd}
              onDragStart={handleDragStart}
              value={group.id}
              // whileDrag={{ padding: '0 16px' }}
              {...Motions.Fade}
              {...STagNew(Sel.MainRoomBottomTrayPresets, 'group', group.id)}
            >
              {/* Be very careful w/ memoization here! */}
              <Group
                group={group}
                handler={passthru}
                isDeletable={groups.length > 1}
                isSelected={selectedId === group.id}
              />
            </Reorder.Item>
          );
        })}
      </Reorder.Group>

      <div className='flex h-full w-[235px] flex-row-reverse'>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <DropdownMenu.Ikon className='self-center' icon='plus' />
          </DropdownMenu.Trigger>

          <DropdownMenu.Content align='end' side='left'>
            <DropdownMenu.Item icon='settings' label='Add preset group' onSelect={handler.onAdd} />
            <DropdownMenu.Item icon='settings' label='Add preset' onSelect={handler.onAddPreset} />
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </div>
    </div>
  );
};

/** Group `<List />` component. */
export const List = memo(Inner, dequal);
